// pro.rejouis.fr — connexion de la boutique via son compte client Shopify (Customer Account
// API, OAuth 2.0 + PKCE, client public partagé avec re.rejouis ; l'URL de retour
// https://pro.rejouis.fr/callback doit être déclarée dans Shopify).
//  1. login()        → PKCE, redirection vers la page de connexion Shopify
//  2. /callback      → handleCallback() envoie le code à `customer-auth`, qui échange le token,
//                      lit l'e-mail et vérifie auprès du lab que c'est une boutique partenaire
//  3. session        → { compte, token signé } en localStorage (12 h)

const SHOP_ID = import.meta.env.VITE_SHOPIFY_SHOP_ID;
const CLIENT_ID = import.meta.env.VITE_SHOPIFY_CUSTOMER_CLIENT_ID;
const SCOPES = 'openid email customer-account-api:full';
const COMPTE_KEY = 'pro_compte';
const TOKEN_KEY = 'pro_session';
const ID_TOKEN_KEY = 'pro_id_token'; // jeton d'identité Shopify, sert uniquement à la déconnexion
const RECONNEXION_KEY = 'pro_reconnexion';

export const sessionToken = () => localStorage.getItem(TOKEN_KEY) || '';

const redirectUri = () => window.location.origin + '/callback';

function b64url(buf) {
  let str = '';
  for (const b of new Uint8Array(buf)) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function login() {
  if (!SHOP_ID || !CLIENT_ID) throw new Error('Connexion non configurée.');
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('oauth_state', state);

  const url = new URL(`https://shopify.com/authentication/${SHOP_ID}/oauth/authorize`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('scope', SCOPES);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.href = url.toString();
}

// Erreur « acces_refuse » : compte Shopify valide mais e-mail sans espace pro (err.email).
export async function handleCallback() {
  const p = new URLSearchParams(window.location.search);
  if (p.get('error')) throw new Error(`Connexion refusée (${p.get('error')}).`);
  const code = p.get('code');
  if (!code) return null;
  if (p.get('state') !== sessionStorage.getItem('oauth_state')) throw new Error('Lien de connexion expiré, recommencez.');

  const res = await fetch('/api/customer-auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, verifier: sessionStorage.getItem('pkce_verifier'), redirectUri: redirectUri() }),
  });
  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('oauth_state');
  const data = await res.json().catch(() => ({}));
  if (data.idToken) localStorage.setItem(ID_TOKEN_KEY, data.idToken);
  if (!res.ok) {
    const err = new Error(data.error || `Erreur ${res.status}`);
    err.email = data.email;
    throw err;
  }
  localStorage.setItem(COMPTE_KEY, JSON.stringify(data.compte));
  localStorage.setItem(TOKEN_KEY, data.session);
  return data.compte;
}

// Aperçu staff : /apercu#<jeton signé par le lab> → session en lecture seule, sans Shopify.
export async function ouvrirApercu() {
  const jeton = window.location.hash.slice(1);
  window.history.replaceState({}, '', '/apercu'); // le jeton ne reste pas dans l'historique
  const res = await fetch('/api/apercu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jeton }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Erreur ${res.status}`);
  logout();
  localStorage.setItem(COMPTE_KEY, JSON.stringify(data.compte));
  localStorage.setItem(TOKEN_KEY, data.session);
  return data.compte;
}

// Changer d'adresse e-mail : Shopify garde sa propre session ouverte, un simple login()
// reconnecterait le même compte. On ferme donc aussi la session Shopify (déconnexion OIDC,
// retour sur l'origine, déclarée comme « Logout URI » dans Shopify), puis on relance la
// connexion au retour (drapeau RECONNEXION_KEY lu par reprendreReconnexion).
// reconnecter:false → simple déconnexion complète (bouton de l'en-tête).
export function autreAdresse({ reconnecter = true } = {}) {
  const idToken = localStorage.getItem(ID_TOKEN_KEY);
  logout();
  if (!idToken) {
    if (reconnecter) login(); else window.location.replace('/');
    return;
  }
  if (reconnecter) sessionStorage.setItem(RECONNEXION_KEY, '1');
  const url = new URL(`https://shopify.com/authentication/${SHOP_ID}/logout`);
  url.searchParams.set('id_token_hint', idToken);
  url.searchParams.set('post_logout_redirect_uri', window.location.origin);
  window.location.href = url.toString();
}

// Au chargement : retour de la déconnexion Shopify demandée par autreAdresse().
export function reprendreReconnexion() {
  if (sessionStorage.getItem(RECONNEXION_KEY) !== '1') return false;
  sessionStorage.removeItem(RECONNEXION_KEY);
  login();
  return true;
}

function sessionValide() {
  try {
    const { payload } = JSON.parse(atob(sessionToken().replace(/-/g, '+').replace(/_/g, '/')));
    return Date.now() < JSON.parse(payload).exp;
  } catch { return false; }
}

export function compteConnecte() {
  let compte = null;
  try { compte = JSON.parse(localStorage.getItem(COMPTE_KEY)); } catch { compte = null; }
  if (compte && !sessionValide()) { logout(); return null; }
  return compte;
}

export function logout() {
  localStorage.removeItem(COMPTE_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);
}
